import React, { Component } from 'react';
import { connect } from 'react-redux';

import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';

import * as Utils from './FileAcceptorUtil';
import FileDragTarget from './FileDragTarget';
import FileUploaded from './FileUploaded';

import { fetchJob } from '../../redux/actions';
import { createJob } from '../../api/client';

import { Alert } from '../popupAlerts';
import { poll } from '@hotglue/common';
import FileUploadIcon from '../../assets/file-upload.svg';

class  FileAcceptor extends Component {
  state = {
    uploadedFiles: [],
    files: [],
    isUpdating: false,
    confirmingDelete: false,
    jobPending: false,
  };

  constructor(props) {
    super();

    this.onDrop = this.onDrop.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleRemoveFile = this.handleRemoveFile.bind(this);
  }

  componentDidMount() {
    Utils.loadUploadedFiles(this);
  }

  suppress(evt) {
    evt.stopPropagation();
    evt.preventDefault();
  }

  onDrop(evt) {
    evt.stopPropagation();
    evt.preventDefault();

    const files = evt.dataTransfer.files;
    this.setState({ files: Object.values(files) });
  }

  handleChange(e) {
    let files = Array.from(e.target.files);

    if ((this.props?.enforceFileTypes ?? []).length > 0) {
      const types = this.props.enforceFileTypes.map((ext) => ext.toLowerCase());
      files = files.filter((file) => {
        return types.includes(file.type);
      });
    }

    this.setState({ files: [...this.state.files, ...Object.values(files)] });
  }

  handleRemoveFile(file) {
    const { files } = this.state;
    const newFiles = files.filter((f) => f !== file);
    this.setState({ files: newFiles });
  }

  showPreviousFiles(classes) {
    const { files, uploadedFiles, isUpdating } = this.state;
    return (
      <Box display="flex" flexDirection="column" width="100%">
        <Typography variant="h2">Files On Server</Typography>
        {uploadedFiles.map((f, i) => (
          <FileUploaded key={i} name={f} uploaded={true} remove={null} />
        ))}
      </Box>
    );
  }

  showNewFiles(classes) {
    const { files, uploadedFiles, isUpdating } = this.state;
    return (
      <Box display="flex" flexDirection="column" width="100%">
        <Box
          display="flex"
          justifyContent="space-between"
          position="sticky"
          top={0}
          sx={{
            bgcolor: ({ palette }) => palette.background.paper,
          }}
          zIndex={10}
        >
          <Typography variant="h2">Newly Uploaded</Typography>
          <Typography variant="body2">{files.length} selected files </Typography>
        </Box>
        {files.map((f, i) => (
          <FileUploaded key={i} name={f.name} uploaded={true} remove={() => this.handleRemoveFile(f)} />
        ))}
      </Box>
    );
  }

  render() {
    const self = this;
    const { files, uploadedFiles, isUpdating, confirmingDelete, jobPending } = this.state;
    const { classes, jobsEnabled, source, enforceFileTypes } = this.props;

    const anyFilesUploaded = (uploadedFiles && uploadedFiles.length != 0) || (files && files.length != 0);

    const showPreviousFiles = uploadedFiles && uploadedFiles.length != 0;
    const showNewFiles = files && files.length != 0;

    let fileAccept = undefined;

    if ((enforceFileTypes ?? []).length > 0) {
      fileAccept = enforceFileTypes.join(', ');
    }

    return (
      <Box display="flex" flex={1} width="100%" flexDirection="column">
        <Paper elevation={0} sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          width: '100%',
          border: (theme) => `0.5px dashed ${theme.palette.text.light}`,
          position: 'relative',
          paddingRight: '16px',
          paddingLeft: '16px',
          paddingTop: '24px',
          paddingBottom: '24px',
          marginBottom: '16px',
        }}>
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1} gap={2}>
            <Box component="img" src={FileUploadIcon} width={48} height={41} flex={1} />
            <Box display="flex" flexDirection="column" alignItems="center" gap={1} flex={1}>
              <Typography variant="h2" textAlign="center">
                Drag Files here or Click to Upload
              </Typography>
              <Typography variant="body1" textAlign="center" color="text.lightish">
                You can select one or more files to import.
              </Typography>
              {
                (enforceFileTypes ?? []).length > 0 && (
                  <Typography variant="body1" textAlign="center" color="text.lightish">
                    Allowed file types: {enforceFileTypes.join(', ')}
                  </Typography>
                )
              }
            </Box>
            {anyFilesUploaded && (
              <Box
                display="grid"
                gridTemplateColumns="repeat(2, 1fr)"
                width="100%"
                gap={2}
                sx={{ cursor: 'default' }}
                flex={2}
              >
                <Box>{showPreviousFiles && self.showPreviousFiles(classes)}</Box>
                <Box maxHeight={168} overflow="auto" zIndex={10000}>
                  {showNewFiles && self.showNewFiles(classes)}
                </Box>
              </Box>
            )}
            <input
              id="data-file"
              type="file"
              accept={fileAccept}
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                top: 0,
                left: 0,
                opacity: 0,
                cursor: 'pointer',
              }}
              onChange={self.handleChange}
              multiple
            />
          </Box>
        </Paper>
        <Box display="flex" justifyContent="space-between" alignItems="center" py={2}>
          <Button
            variant="outlined"
            color="text"
            disabled={!uploadedFiles?.length || isUpdating}
            sx={{ px: 2, opacity: 0.75 }}
            onClick={() => Utils.handleRemoteClear(self)}
          >
            Clear All
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={files.length == 0 || !anyFilesUploaded || isUpdating}
            onClick={() => Utils.onSubmit(self)}
            sx={{ px: 4 }}
          >
            {isUpdating && <CircularProgress size={24} sx={{ mr: 2 }} />}
            {isUpdating ? 'Uploading...' : 'Use Files'}
          </Button>
        </Box>
      </Box>
    );
  }
}

const mapStateToProps = (state, props) => {
  return {
    source: state.sources.selectedSource,
    demo: state.configuration.demo || state.preferences.demoMode,
    enforceFileTypes: state.preferences.enforceFileTypes,
    jobsEnabled: state.preferences.jobsEnabled,
    writeJobsEnabled: state.preferences.writeJobsEnabled,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchJob: () => dispatch(fetchJob()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(FileAcceptor);
